<template>
  <div class="promo-design-group">
    <v-select
      v-model="formType"
      :items="formTypeOptions"
      label="Form Type"
      variant="outlined"
      density="compact"
      hideDetails
      class="mb-4"
    >
      <template #item="{ item, props: itemProps }">
        <v-tooltip
          v-if="item.raw.value === 'billing' && !item.raw.recurlyConfigured"
          text="Enable Recurly integration and add a public key"
          location="right"
        >
          <template #activator="{ props: tooltipProps }">
            <div v-bind="tooltipProps">
              <v-list-item v-bind="itemProps" />
            </div>
          </template>
        </v-tooltip>
        <v-list-item v-else v-bind="itemProps" />
      </template>
    </v-select>
    <div v-if="formType === 'inputs'" class="inputs-wrapper">
      <div
        v-for="inputConfig in inputConfigs"
        :key="inputConfig.labelAction"
        style="padding-top: 18px"
      >
        <div class="promo-inputs-fields-div">
          <v-text-field
            v-model.trim="
              (currentTranslations.hasOwnProperty(inputConfig.labelAction)
                ? previewActions
                : actions)[inputConfig.labelAction]
            "
            variant="outlined"
            density="compact"
            persistentPlaceholder
            hideDetails=""
            :label="inputConfig.labelWording"
            :disabled="isLocalized && currentTranslations.hasOwnProperty(inputConfig.labelAction)"
            placeholder="e.g.: Email address"
          />
          <v-text-field
            v-model.trim="
              (currentTranslations.hasOwnProperty(inputConfig.placeholderAction)
                ? previewActions
                : actions)[inputConfig.placeholderAction]
            "
            variant="outlined"
            density="compact"
            persistentPlaceholder
            hideDetails=""
            :label="inputConfig.placeholderWording"
            :disabled="
              isLocalized && currentTranslations.hasOwnProperty(inputConfig.placeholderAction)
            "
          />
        </div>
        <div class="promo-inputs-fields-div" style="padding-top: 15px">
          <v-select
            v-model="actions[inputConfig.typeAction]"
            hideDetails
            variant="outlined"
            density="compact"
            :label="inputConfig.typeWording"
            :items="typeItems"
            itemTitle="text"
            placeholder="Select type"
          />
          <v-select
            v-model="actions[inputConfig.widthAction]"
            hideDetails
            placeholder="Select width"
            variant="outlined"
            density="compact"
            :label="inputConfig.widthWording"
            itemTitle="text"
            :items="widthOptions"
          />
        </div>
      </div>
    </div>
    <div v-if="formType === 'survey'" class="survey-inputs">
      <div
        class="survey-top-controls mt-5 pt-0 pb-3"
        style="border-bottom: 1px solid rgba(0, 0, 0, 0.38)"
      >
        <v-select
          v-model.number="surveyOptionsTotal"
          class="survey-number-selector"
          hideDetails
          variant="outlined"
          density="compact"
          label="Number of Options"
          :items="surveyOptionsNumberItems"
        ></v-select>
        <v-text-field
          v-model="actions.rf_retention_survey_options_font_size"
          class="survey-number-selector"
          hideDetails
          variant="outlined"
          persistentPlaceholder
          density="compact"
          label="Font size"
          type="text"
        ></v-text-field>
        <v-text-field
          v-model="actions.rf_retention_survey_top_margin"
          class="survey-number-selector"
          hideDetails
          variant="outlined"
          persistentPlaceholder
          density="compact"
          label="Top margin"
          placeholder="e.g.: 16px"
          type="text"
        ></v-text-field>
      </div>
      <div class="survey-options-wrapper mt-3">
        <div v-for="n in surveyOptionsTotal" :key="n" class="survey-options-row">
          <v-text-field
            v-model="
              (currentTranslations.hasOwnProperty(`rf_retention_survey_option_${n}_label`)
                ? previewActions
                : surveyOptionsPreview)[`rf_retention_survey_option_${n}_label`]
            "
            hideDetails
            variant="outlined"
            density="compact"
            persistentPlaceholder
            type="text"
            :label="`Option ${n} Label`"
            :disabled="currentTranslations.hasOwnProperty(`rf_retention_survey_option_${n}_label`)"
          ></v-text-field>
          <v-text-field
            v-model="surveyOptionsPreview[`rf_retention_survey_option_${n}_value`]"
            hideDetails
            variant="outlined"
            density="compact"
            persistentPlaceholder
            type="text"
            :label="`Option ${n} Value`"
          ></v-text-field>
        </div>
      </div>
      <div v-if="isRokuSurvey" class="survey-roku-button-colors mt-4">
        <div class="grid grid-cols-2 gap-x-2 gap-y-4">
          <v-text-field
            v-model.trim="actions.button1_bg_color"
            hideDetails
            variant="outlined"
            density="compact"
            persistentPlaceholder
            label="Button 1 Color"
            placeholder="e.g.: #336699"
            bgColor="#ffffff"
            @blur="actions.button1_bg_color = extendHex(actions.button1_bg_color)"
          ></v-text-field>
          <v-text-field
            v-model.trim="actions.button1_text_color"
            variant="outlined"
            density="compact"
            persistentPlaceholder
            label="Button 1 Text Color"
            placeholder="e.g.: #FFFFFF"
            hideDetails
            bgColor="#ffffff"
            @blur="actions.button1_text_color = extendHex(actions.button1_text_color)"
          ></v-text-field>
          <v-text-field
            v-model.trim="actions.button1_highlight_color"
            hideDetails
            variant="outlined"
            density="compact"
            persistentPlaceholder
            label="Button Text Highlight"
            placeholder="e.g.: #FFFF00"
            bgColor="#ffffff"
            @blur="actions.button1_highlight_color = extendHex(actions.button1_highlight_color)"
          ></v-text-field>
          <v-text-field
            v-model.trim="actions.button1_focus_bg_color"
            variant="outlined"
            density="compact"
            persistentPlaceholder
            label="Button 1 Focus Color"
            placeholder="e.g.: #FFFFFF"
            hideDetails
            bgColor="#ffffff"
            @blur="actions.button1_focus_bg_color = extendHex(actions.button1_focus_bg_color)"
          ></v-text-field>
        </div>
      </div>
    </div>
    <div v-if="formType === 'billing'" class="billing-wrapper">
      <div class="text-caption text-medium-emphasis px-4 py-2">
        A billing update form will be displayed in this prompt, e.g. to manage payment failures.
        Credit card processing is securely handled by Recurly.
      </div>
    </div>
  </div>
</template>

<script>
import { useAppsStore } from "@/pinia/appsStore";
import LanguagePreviewMixin from "@/utils/LanguagePreviewMixin";
import { DefaultDeviceOptions } from "@/utils/constants/DevicesConstants";
import { extendHex } from "@/utils/colorHelpers";

export default {
  name: "RfRetentionFieldSet",
  mixins: [LanguagePreviewMixin],
  props: ["actions", "model", "requireAcceptButton"],
  setup: () => ({ appsStore: useAppsStore() }),
  data() {
    return {
      widthOptions: [
        { text: "100%", value: "100%" },
        { text: "75%", value: "75%" },
        { text: "50%", value: "50%" },
      ],
      inputFormEnabled: null,
      surveyOptionsNumberItems: [2, 3, 4, 5],
      surveyOptionsTotal: 0,
      surveyOptionsPreview: {
        rf_retention_survey_option_1_label: null,
        rf_retention_survey_option_1_value: null,
        rf_retention_survey_option_2_label: null,
        rf_retention_survey_option_2_value: null,
        rf_retention_survey_option_3_label: null,
        rf_retention_survey_option_3_value: null,
        rf_retention_survey_option_4_label: null,
        rf_retention_survey_option_4_value: null,
        rf_retention_survey_option_5_label: null,
        rf_retention_survey_option_5_value: null,
      },
    };
  },
  computed: {
    typeItems() {
      const items = [
        { text: "Email", value: "email" },
        { text: "Number", value: "number" },
        { text: "Text", value: "text" },
      ];
      if (this.addressEnabled) {
        items.push({ text: "Address", value: "address" });
      }
      items.push({ text: "None", value: null });
      return items;
    },
    surveyEnabled() {
      const pathType = this.model.path_type;
      return ["retention_modal", "interstitial", "video"].indexOf(pathType) > -1;
    },
    isRokuSurvey() {
      return this.model.device_type === DefaultDeviceOptions.roku_os.value;
    },
    addressEnabled() {
      const key = this.appsStore.app?.providers?.google_play?.maps_api_key;
      const active = this.appsStore.app?.flags?.google_maps_active;
      return key && active;
    },
    formType: {
      get() {
        if ([true, "true"].includes(this.actions.rf_billing_update_enabled)) return "billing";
        if ([true, "true"].includes(this.actions.rf_retention_survey_selected)) return "survey";
        if (this.inputFormEnabled) return "inputs";
        return "none";
      },
      set(value) {
        this.actions.rf_billing_update_enabled = value === "billing" ? "true" : "false";
        this.actions.rf_retention_survey_selected = value === "survey" ? "true" : "false";
        this.inputFormEnabled = value === "inputs";
      },
    },
    formTypeOptions() {
      const { app } = this.appsStore;
      const billingUpdateEnabled =
        app?.flags?.in_prompt_billing &&
        ["retention_modal", "interstitial"].includes(this.model.path_type);
      const disabled = this.isDisabled;
      const options = [
        { title: "None", value: "none", props: { disabled } },
        { title: "Inputs", value: "inputs", props: { disabled } },
      ];
      if (this.surveyEnabled) {
        options.push({ title: "Survey", value: "survey", props: { disabled } });
      }
      if (billingUpdateEnabled) {
        const recurly = app?.providers?.recurly;
        const recurlyConfigured = recurly?.active && recurly?.public_key;
        options.push({
          title: "Recurly Billing Info Update",
          value: "billing",
          recurlyConfigured,
          props: { disabled: disabled || !recurlyConfigured },
        });
      }
      return options;
    },
    isDisabled() {
      if (this.requireAcceptButton) {
        return this.actions.rf_settings_tile_interaction !== "button" || !this.confirmText;
      }
      return false;
    },
    confirmText() {
      return this.isLocalized
        ? this.previewActions.rf_retention_button1_text
        : this.actions.rf_retention_button1_text;
    },
    hasMultipleInputs() {
      const pathType = this.model.path_type;
      return (
        pathType === "retention_modal" ||
        pathType === "interstitial" ||
        pathType === "video" ||
        pathType === "widget"
      );
    },
    inputConfigs() {
      let configs = [
        {
          labelAction: "rf_retention_promo_input_1_label",
          typeAction: "rf_retention_promo_input_1_type",
          widthAction: "rf_retention_promo_input_1_width",
          labelWording: "First Input Label",
          typeWording: "First Input Type",
          widthWording: "First Input Width",
          placeholderAction: "rf_retention_promo_input_1_placeholder",
          placeholderWording: "First Input Placeholder",
        },
      ];
      if (this.hasMultipleInputs) {
        configs = configs.concat([
          {
            labelAction: "rf_retention_promo_input_2_label",
            typeAction: "rf_retention_promo_input_2_type",
            widthAction: "rf_retention_promo_input_2_width",
            labelWording: "Second Input Label",
            typeWording: "Second Input Type",
            widthWording: "Second Input Width",
            placeholderAction: "rf_retention_promo_input_2_placeholder",
            placeholderWording: "Second Input Placeholder",
          },
          {
            labelAction: "rf_retention_promo_input_3_label",
            typeAction: "rf_retention_promo_input_3_type",
            widthAction: "rf_retention_promo_input_3_width",
            labelWording: "Third Input Label",
            typeWording: "Third Input Type",
            widthWording: "Third Input Width",
            placeholderAction: "rf_retention_promo_input_3_placeholder",
            placeholderWording: "Third Input Placeholder",
          },
        ]);
      }
      return configs;
    },
  },
  watch: {
    inputFormEnabled(to) {
      if (!to) {
        if (!this.isLocalized) {
          this.actions.rf_retention_promo_input_1_label = null;
          this.actions.rf_retention_promo_input_2_label = null;
          this.actions.rf_retention_promo_input_3_label = null;
          this.actions.rf_retention_promo_input_1_placeholder = null;
          this.actions.rf_retention_promo_input_2_placeholder = null;
          this.actions.rf_retention_promo_input_3_placeholder = null;
        }
        this.actions.rf_retention_promo_input_1_type = null;
        this.actions.rf_retention_promo_input_1_width = null;
        this.actions.rf_retention_promo_input_2_type = null;
        this.actions.rf_retention_promo_input_2_width = null;
        this.actions.rf_retention_promo_input_3_type = null;
        this.actions.rf_retention_promo_input_3_width = null;
        this.$emit("forceRender");
      }
    },
    surveyOptionsTotal(to, from) {
      this.actions.rf_retention_survey_options_total = to;

      if (to < from) {
        // if the number is decreased, clear excess [`to+1`:5] options from actions
        for (let n = to + 1; n <= 5; n += 1) {
          this.actions[`rf_retention_survey_option_${n}_label`] = null;
          this.actions[`rf_retention_survey_option_${n}_value`] = null;
        }
      } else if (from < to) {
        // if the number is increased, add to the excess [`from+1`:`to`] options from preview
        for (let i = from + 1; i <= to; i += 1) {
          this.actions[`rf_retention_survey_option_${i}_label`] =
            this.surveyOptionsPreview[`rf_retention_survey_option_${i}_label`];
          this.actions[`rf_retention_survey_option_${i}_value`] =
            this.surveyOptionsPreview[`rf_retention_survey_option_${i}_value`];
        }
      }
    },
    surveyOptionsPreview: {
      handler(to) {
        const optionsLimit = this.actions.rf_retention_survey_options_total;

        for (let n = 1; n <= optionsLimit; n += 1) {
          this.actions[`rf_retention_survey_option_${n}_label`] =
            to[`rf_retention_survey_option_${n}_label`];
          this.actions[`rf_retention_survey_option_${n}_value`] =
            to[`rf_retention_survey_option_${n}_value`];
        }
      },
      deep: true,
    },
  },
  created() {
    this.inputFormEnabled = !!this.readOnlyActions.rf_retention_promo_input_1_label;
    this.surveyOptionsTotal = +this.actions.rf_retention_survey_options_total || 3;
    for (let n = 1; n <= this.surveyOptionsTotal; n += 1) {
      this.surveyOptionsPreview[`rf_retention_survey_option_${n}_label`] =
        this.actions[`rf_retention_survey_option_${n}_label`];
      this.surveyOptionsPreview[`rf_retention_survey_option_${n}_value`] =
        this.actions[`rf_retention_survey_option_${n}_value`];
    }
  },
  methods: {
    extendHex,
  },
};
</script>
